import * as THREE from "three";
import type { Rng } from "./rng";
import { WORLD } from "./config";
import { buildGround, buildLeaves, buildRocks, buildTufts } from "./world/ground";
import { terrainHeight } from "./world/terrain";
import { buildTree, SPECIES, type TreeAsset } from "./flora/tree";
import { buildCharacter, type Character } from "./actors/character";
import { buildTown } from "./world/town";
import type { Building } from "./world/buildings";
import { Ground } from "./world/walkable";
import { buildCampfire, type Fire } from "./world/campfire";
import { buildChest, type Chest } from "./world/chest";
import { CHEST, FIRE } from "./config";

export interface SceneBuild {
  scene: THREE.Scene;
  character: Character;
  trees: TreeAsset[];
  buildings: Building[];
  ground: Ground;
  fire: Fire;
  chest: Chest;
}

interface Placement {
  x: number;
  z: number;
  radius: number;
}

const TREE_COUNT = 48;

const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

function placeTrees(
  rng: Rng,
  count: number,
  clearRadius: number,
  exclusions: readonly Placement[],
): Placement[] {
  const placements: Placement[] = [];
  const limit = WORLD.groundSize * 0.26;

  for (let i = 0; i < count; i++) {
    for (let attempt = 0; attempt < 40; attempt++) {
      const x = rng.range(-limit, limit);
      const z = rng.range(-limit, limit);
      const radius = rng.range(1.6, 3.2);
      if (Math.hypot(x, z) < clearRadius + radius) continue;
      if (exclusions.some((zone) => Math.hypot(zone.x - x, zone.z - z) < zone.radius + radius)) {
        continue;
      }
      const clash = placements.some(
        (other) => Math.hypot(other.x - x, other.z - z) < other.radius + radius + 0.7,
      );
      if (clash) continue;
      placements.push({ x, z, radius });
      break;
    }
  }
  return placements;
}

export async function buildSceneStaged(
  rng: Rng,
  onProgress: (fraction: number) => void,
): Promise<SceneBuild> {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0.42);
  const ground = new Ground();
  const trees: TreeAsset[] = [];

  let character: Character | null = null;
  let fire: Fire | null = null;
  let chest: Chest | null = null;
  let buildings: Building[] = [];
  let placements: Placement[] = [];
  let footprint: (x: number, z: number) => boolean = () => false;
  let grown = 0;

  const growTrees = (count: number): void => {
    for (let i = 0; i < count && grown < placements.length; i++, grown++) {
      const placement = placements[grown] as Placement;
      const species = rng.weighted(
        [SPECIES.OAK, SPECIES.BIRCH, SPECIES.HAWTHORN, SPECIES.WILLOW, SPECIES.PINE],
        [3.2, 2.1, 2.4, 1.6, 2.4],
      );
      const tree = buildTree(rng, species);
      tree.group.position.set(
        placement.x,
        terrainHeight(placement.x, placement.z) - 0.08,
        placement.z,
      );
      tree.group.rotation.y = rng.range(0, Math.PI * 2);
      tree.group.scale.setScalar(rng.range(0.78, 1.14));
      scene.add(tree.group);
      trees.push(tree);
    }
  };

  const steps: Array<() => void> = [
    () => {
        const town = buildTown(rng.fork(23), ground);
        scene.add(town.group);
        buildings = town.buildings;
        footprint = (x: number, z: number): boolean =>
          town.buildings.some(
            (building) => Math.hypot(building.x - x, building.z - z) < 7.5,
          ) || Math.hypot(town.centre[0] - x, town.centre[1] - z) < town.radius * 0.94;
        placements = placeTrees(rng.fork(11), TREE_COUNT, 2.6, [
          { x: town.centre[0], z: town.centre[1], radius: town.radius + 2.2 },
          ...town.buildings.map((building) => ({
            x: building.x,
            z: building.z,
            radius: 6.8,
          })),
      ]);
    },
    () => scene.add(buildGround()),
    () => scene.add(buildLeaves(rng.fork(4))),
    () => scene.add(buildTufts(rng.fork(5), footprint)),
    () => scene.add(buildRocks(rng.fork(7))),
  ];

  const chunks = 6;
  const perChunk = Math.ceil(TREE_COUNT / chunks);
  for (let i = 0; i < chunks; i++) {
    steps.push(() => growTrees(perChunk));
  }

  steps.push(() => {
    fire = buildCampfire(rng.fork(31));
    const [fx, fz] = FIRE.spot;
    fire.group.position.set(fx, terrainHeight(fx, fz) - 0.02, fz);
    scene.add(fire.group);
  });

  steps.push(() => {
    chest = buildChest(rng.fork(41));
    const [cx, cz] = CHEST.spot;
    chest.group.position.set(cx, ground.heightAt(cx, cz) - 0.01, cz);
    // set it down facing the plaza, so you meet its front as you come off the stones
    chest.group.rotation.y = Math.atan2(0 - cx, -6 - cz);
    scene.add(chest.group);
  });

  steps.push(() => {
    character = buildCharacter(rng.fork(17));
    character.root.position.set(0, terrainHeight(0, 0), 0);
    scene.add(character.root);
  });

  for (let i = 0; i < steps.length; i++) {
    onProgress(i / steps.length);
    await nextFrame();
    (steps[i] as () => void)();
  }
  onProgress(1);

  if (!character || !fire || !chest) throw new Error("scene: build incomplete");
  return { scene, character, trees, buildings, ground, fire, chest };
}
